import React, { useId, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';

const SCROLLABLE_OVERFLOW = /auto|scroll|overlay/;
const GAP_X = 15;
const GAP_Y = 4;

const getScrollableAncestors = (element) => {
    const ancestors = [];
    let node = element?.parentElement;
    while (node) {
        const { overflow, overflowX, overflowY } = window.getComputedStyle(node);
        if (
            SCROLLABLE_OVERFLOW.test(overflow)
            || SCROLLABLE_OVERFLOW.test(overflowX)
            || SCROLLABLE_OVERFLOW.test(overflowY)
        ) {
            ancestors.push(node);
        }
        node = node.parentElement;
    }
    return ancestors;
};

/**
 * Custom tour dimming layer that tracks the improvement card on scroll.
 * Unlike rc-tour's mask, this overlay never intercepts wheel or pointer events.
 */
const ImprovementModeTourSpotlight = ({ open, targetRef }) => {
    const maskId = useId();
    const [pos, setPos] = useState(null);

    useLayoutEffect(() => {
        if (!open) {
            setPos(null);
            return undefined;
        }

        let rafId = null;
        let lastRect = null;
        const scrollTargets = new Set();

        // Coalesce measurements instead of cancel-and-reschedule: scroll/resize
        // events fire before rAF callbacks within a frame, so cancelling the
        // pending frame on every event can starve listeners that measure the
        // same way (the tour popover) forever.
        const updatePosition = () => {
            if (rafId != null) {
                return;
            }
            rafId = requestAnimationFrame(() => {
                rafId = null;
                const el = targetRef?.current;
                if (!el) {
                    setPos(null);
                    return;
                }
                const { left, top, width, height } = el.getBoundingClientRect();
                setPos({
                    left: left - GAP_X,
                    top: top - GAP_Y,
                    width: width + GAP_X * 2,
                    height: height + GAP_Y * 2,
                });
                // Keep the tour popover aligned with the target while nested
                // containers scroll — but only when the target actually moved,
                // so this doesn't self-perpetuate an endless resize loop.
                const moved = !lastRect
                    || lastRect.left !== left
                    || lastRect.top !== top
                    || lastRect.width !== width
                    || lastRect.height !== height;
                lastRect = { left, top, width, height };
                if (moved) {
                    window.dispatchEvent(new Event('resize'));
                }
            });
        };

        const bindScrollListeners = () => {
            scrollTargets.clear();
            scrollTargets.add(window);
            const el = targetRef?.current;
            if (el) {
                getScrollableAncestors(el).forEach((node) => scrollTargets.add(node));
            }
            scrollTargets.forEach((target) => {
                target.addEventListener('scroll', updatePosition, { passive: true });
            });
        };

        bindScrollListeners();
        window.addEventListener('resize', updatePosition);
        updatePosition();

        const retryId = !targetRef?.current
            ? requestAnimationFrame(() => {
                bindScrollListeners();
                updatePosition();
            })
            : null;

        return () => {
            if (retryId != null) {
                cancelAnimationFrame(retryId);
            }
            if (rafId != null) {
                cancelAnimationFrame(rafId);
            }
            scrollTargets.forEach((target) => {
                target.removeEventListener('scroll', updatePosition);
            });
            window.removeEventListener('resize', updatePosition);
        };
    }, [open, targetRef]);

    if (!open || !pos) {
        return null;
    }

    return createPortal(
        <div
            aria-hidden
            className="improvement-mode-tour-spotlight"
            style={{
                position: 'fixed',
                inset: 0,
                zIndex: 1400,
                pointerEvents: 'none',
            }}
        >
            <svg width="100%" height="100%" style={{ display: 'block', pointerEvents: 'none' }}>
                <defs>
                    <mask id={maskId}>
                        <rect x="0" y="0" width="100%" height="100%" fill="white" />
                        <rect
                            x={pos.left}
                            y={pos.top}
                            width={pos.width}
                            height={pos.height}
                            rx={8}
                            fill="black"
                        />
                    </mask>
                </defs>
                <rect
                    x="0"
                    y="0"
                    width="100%"
                    height="100%"
                    fill="rgba(0, 0, 0, 0.5)"
                    mask={`url(#${maskId})`}
                    style={{ pointerEvents: 'none' }}
                />
            </svg>
        </div>,
        document.body,
    );
};

export default ImprovementModeTourSpotlight;
